'use client';

import { MoreVertical } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { type ReactNode } from 'react';

import { getS3ObjectURL } from '../../utils/s3';
import { BUTTON_PILL_PADDING } from './Button';

const DEFAULT_BANNER = '/images/header-image-default.jpg';

/**
 * Every banner draws its action row from the same three pieces so the pages
 * stop inventing their own button vocabulary:
 *
 *   BannerAction — neutral secondary pill (History, Chat, Message, …)
 *   <Button>     — exactly ONE per banner for the primary call to action
 *                  (Follow / Join Space), or DANGER for its inverse
 *   BannerMenu   — the ⋮ dropdown trigger
 *
 * Matches Button's own SECONDARY variant, but works as a <Link> too, so a
 * navigating action doesn't have to nest a <button> inside an <a>.
 *
 * The padding has to stay in step with Button's `px-6 py-2.5`, which is why it
 * comes from BUTTON_PILL_PADDING rather than being written out here. It was
 * `px-4 py-2` and drifted unnoticed, so an action row mixing the two — History
 * and Message from BannerAction, Unfollow from <Button> — rendered the Button
 * 4px taller than its neighbours.
 */
export const BANNER_ACTION_CLASS = `inline-flex items-center gap-1.5 rounded-full border border-ds-border-standard bg-background-light ${BUTTON_PILL_PADDING} font-emphasis text-textPrimary transition-colors duration-300 hover:bg-ds-border-hover`;

export const BannerAction = ({
  href,
  onClick,
  icon,
  text,
  title,
  dataId
}: {
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
  text: string;
  title?: string;
  dataId?: string;
}) =>
  href ? (
    <Link
      href={href}
      title={title ?? text}
      className={BANNER_ACTION_CLASS}
      data-id={dataId}
    >
      {icon}
      {text}
    </Link>
  ) : (
    <button
      onClick={onClick}
      title={title ?? text}
      className={BANNER_ACTION_CLASS}
      data-id={dataId}
    >
      {icon}
      {text}
    </button>
  );

/** The ⋮ trigger. Callers render their own dropdown into `children`. */
export const BannerMenu = ({
  open,
  onToggle,
  label,
  children,
  menuRef
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  children: ReactNode;
  menuRef?: React.RefObject<HTMLDivElement | null>;
}) => (
  <div className="relative self-center" ref={menuRef}>
    <button
      title={label}
      aria-label={label}
      onClick={onToggle}
      className="flex size-10 items-center justify-center rounded-full text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary"
    >
      <MoreVertical />
    </button>
    {open && children}
  </div>
);

/** A counted figure in the stats row: muted label, then the value. */
export const BannerStat = ({
  label,
  value,
  onClick
}: {
  label: string;
  value?: ReactNode;
  onClick?: () => void;
}) => (
  // 20px of text is too short to tap; the `after` box extends the hit area to
  // 44px without moving the row.
  <button
    className="relative flex gap-2 after:absolute after:-inset-y-3 after:inset-x-0 after:content-[''] hover:text-primary"
    onClick={onClick}
  >
    <span className="text-textSecondary/60">{label}</span>
    {value}
  </button>
);

/** Small bordered pill used in the subtitle row (owner, space, PRIVATE). */
export const BannerChip = ({
  href,
  icon,
  text,
  title,
  tone = 'neutral',
  dataId
}: {
  href?: string;
  icon?: ReactNode;
  text: string;
  title?: string;
  tone?: 'neutral' | 'danger';
  dataId?: string;
}) => {
  const className = `flex min-w-0 max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
    tone === 'danger'
      ? 'border-destructive/20 bg-destructive/10 font-emphasis tracking-wide text-destructive'
      : 'border-ds-border-standard bg-background-primary text-textSecondary hover:border-primary/50 hover:text-textPrimary'
  }`;
  const inner = (
    <>
      {icon}
      <span className="truncate">{text}</span>
    </>
  );
  return href ? (
    <Link href={href} title={title} className={className} data-id={dataId}>
      {inner}
    </Link>
  ) : (
    <span title={title} className={className} data-id={dataId}>
      {inner}
    </span>
  );
};

/**
 * Which entity the banner is describing. Only drives the handful of cosmetic
 * differences between the three pages (see the ternaries below) — everything
 * structural is shared.
 */
export type PageBannerVariant = 'user' | 'space' | 'factiii';

type Props = {
  variant: PageBannerVariant;
  /**
   * Identifies the entity (username / slug / id). Combined with the banner and
   * avatar keys to force a remount when you navigate between two entities:
   * React otherwise reuses the <img> element on a src swap and the previous
   * entity's banner stays painted until the new one finishes loading.
   */
  entityKey: string;
  bannerKey?: string | null;
  bannerAlt: string;
  /** The <Avatar>, supplied by the caller since each variant keys it differently. */
  avatar: ReactNode;
  /** Anchored to the avatar's top-left corner — the factiii page's sibling switcher. */
  avatarAside?: ReactNode;
  title: ReactNode;
  /** @handle, owner chip, PRIVATE badge — sits directly under the title. */
  subtitle?: ReactNode;
  /** Bio / description. Wraps and preserves newlines. */
  description?: ReactNode;
  /** Buttons floated over the cover image: Edit, Moderate, Board view. */
  coverActions?: ReactNode;
  /** Primary actions opposite the avatar: Follow, Join, Chat, the ⋮ dropdown. */
  actions?: ReactNode;
  /** Counts row: followers, members, factiii count. */
  stats?: ReactNode;
  /** Full-width rows under the identity block: pinned factiiis, requirement chips. */
  children?: ReactNode;
  /**
   * False when something above the banner (an invite strip, a runner banner)
   * already clears the fixed top bar, so we don't double the gap.
   */
  topOffset?: boolean;
  dataId?: string;
  bannerDataId?: string;
};

const PageBanner = ({
  variant,
  entityKey,
  bannerKey,
  bannerAlt,
  avatar,
  avatarAside,
  title,
  subtitle,
  description,
  coverActions,
  actions,
  stats,
  children,
  topOffset = true,
  dataId,
  bannerDataId
}: Props) => (
  <div data-id={dataId} className={`w-full ${topOffset ? 'mt-16' : ''}`}>
    {/* No overflow-hidden on the card: the ⋮ dropdowns inside `actions` are
        absolutely positioned and would get clipped. The cover clips itself. */}
    <div className="relative rounded-lg bg-background-secondary shadow-lg">
      {/* min-h: the avatar occupies the bottom ~56px of the cover, and the
          overlaid actions need ~72px above it for two wrapped rows. Below
          ~432px wide a pure 3:1 cover is too short for both, and the second
          row of buttons lands on the avatar. */}
      <div className="relative aspect-[3/1] min-h-36 w-full overflow-hidden rounded-t-lg">
        <Image
          key={`${entityKey}-${bannerKey ?? 'default'}`}
          fill
          data-id={bannerDataId}
          src={bannerKey ? getS3ObjectURL(bannerKey) : DEFAULT_BANNER}
          alt={bannerAlt}
          className="object-cover"
          priority
          // Next 16 no longer derives fetchPriority from `priority`, and this
          // cover is the largest paint on space and topic pages.
          fetchPriority="high"
          sizes="100vw"
        />
        {coverActions && (
          // Scrim: the cover is user-supplied, so a bright image would
          // otherwise swallow the CLEAR-variant buttons sitting on it.
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/50 to-transparent" />
        )}
      </div>

      {/* Anchored to the card, not the cover: the cover clips its own overflow,
          so a row that wrapped inside it silently swallowed the last button at
          narrow widths. Out here an overflowing row is at least visible. */}
      {coverActions && (
        <div className="absolute right-3 top-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-wrap items-center justify-end gap-2">
          {coverActions}
        </div>
      )}

      {/* Identity row — the avatar straddles the cover edge and the primary
          actions sit opposite it. `basis-60` is what makes the action list drop
          onto its own full-width line once under ~240px is left beside the
          avatar, instead of stacking into a cramped right-hand column. */}
      <div className="flex flex-wrap items-end justify-between gap-x-3 px-4">
        {/* z-20 beats the cover actions' z-10, so the avatar always wins any
            residual overlap rather than sitting under a button. */}
        <div className="relative z-20 -mt-14 shrink-0">
          {avatar}
          {avatarAside}
        </div>
        {actions && (
          <div className="flex flex-1 basis-60 flex-wrap items-center justify-end gap-2 py-3">
            {actions}
          </div>
        )}
      </div>

      {/* Name, handle and description get the full card width. The old layout
          floated them beside a min-w-28 avatar column, which squeezed long
          names into a one-word-per-line column. */}
      <div className="px-4 pt-2">
        <h1
          className={`break-words text-lg leading-tight ${
            variant === 'factiii' ? 'capitalize' : ''
          }`}
        >
          {title}
        </h1>
        {subtitle && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-textSecondary/60">
            {subtitle}
          </div>
        )}
        {description && (
          <div className="mt-2 whitespace-pre-line break-words text-sm">
            {description}
          </div>
        )}
      </div>

      {stats && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 px-4 text-sm">
          {stats}
        </div>
      )}

      <div className={children ? 'mt-3 space-y-3 pb-4' : 'pb-4'}>
        {children}
      </div>
    </div>
  </div>
);

export default PageBanner;
